import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Edit2, Trash2, X, Plus, ClipboardListIcon } from "lucide-react";
import { useReportTypes } from "../context/ReportTypesContext";

export default function ReportTypesTable() {
  const {
    reportTypes,
    loading,
    addReportType,
    updateReportType,
    deleteReportType,
    toggleReportStatus,
  } = useReportTypes();

  const [searchTerm, setSearchTerm] = useState("");
  const [sortBy, setSortBy] = useState("");
  const [modal, setModal] = useState({ type: null, item: null });

  const filteredItems = reportTypes.filter((r) => {
    const t = searchTerm.toLowerCase();
    return r.title.toLowerCase().includes(t) || r.description.toLowerCase().includes(t) || r.status.includes(t);
  });

  const displayItems = [...filteredItems].sort((a, b) => {
    if (sortBy === "title-asc") return a.title.localeCompare(b.title);
    if (sortBy === "title-desc") return b.title.localeCompare(a.title);
    if (sortBy === "status-asc") return a.status.localeCompare(b.status);
    if (sortBy === "status-desc") return b.status.localeCompare(a.status);
    return 0;
  });

  const closeModal = () => setModal({ type: null, item: null });
  const handleAdd = () => setModal({ type: "add", item: null });

  if (loading) return <div className="p-6">Loading report types...</div>;

  return (
    <div className="space-y-6 px-8 py-4">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 bg-teal-600 rounded-xl flex items-center justify-center shadow-sm">
            <ClipboardListIcon className="w-6 h-6 text-white" />
          </div>
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-teal-800 leading-tight">
              Suggestion & Issue Types
            </h1>
            <p className="mt-1 text-gray-600">
              Manage your Suggestion & Issue Types
            </p>
          </div>
        </div>

        <button
          onClick={handleAdd}
          className="flex items-center gap-2 bg-teal-600 text-white px-5 py-2.5 rounded-xl hover:bg-teal-700 transition-colors shadow-sm"
        >
          <Plus className="w-5 h-5" />
          Add
        </button>
      </div>

      {/* Search + Sort */}
      <div className="flex flex-col md:flex-row gap-4 items-center mb-4">
        <input
          type="text"
          placeholder="Search..."
          className="w-full md:w-96 px-4 py-2 border rounded-xl"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
        />
        <select className="px-4 py-2 border rounded-xl" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
          <option value="">Sort By</option>
          <option value="title-asc">Title: A → Z</option>
          <option value="title-desc">Title: Z → A</option>
          <option value="status-asc">Status: Active → Inactive</option>
          <option value="status-desc">Status: Inactive → Active</option>
        </select>
      </div>

      {/* Table */}
      <div className="overflow-x-auto bg-white rounded-xl shadow-sm border border-gray-200">
        <table className="min-w-full text-left text-sm">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="px-4 py-3">Title</th>
              <th className="px-4 py-3">Description</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {displayItems.map((item) => (
              <tr key={item.id} className="border-t hover:bg-gray-50">
                <td className="px-4 py-3">{item.title}</td>
                <td className="px-4 py-3">{item.description}</td>

                {/* Status Toggle */}
                <td className="px-4 py-3">
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => toggleReportStatus(item)}
                      className={`relative inline-flex items-center w-11 h-6 rounded-full transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-offset-1 ${
                        item.status === "active"
                          ? "bg-green-500 focus:ring-teal-400"
                          : "bg-gray-300 focus:ring-gray-400"
                      }`}
                      title={
                        item.status === "active"
                          ? "Active – click to deactivate"
                          : "Inactive – click to activate"
                      }
                    >
                      <span
                        className={`inline-block w-4 h-4 bg-white rounded-full shadow transform transition-transform duration-200 ${
                          item.status === "active" ? "translate-x-6" : "translate-x-1"
                        }`}
                      />
                    </button>
                    {/* <span
                      className={`text-xs font-medium ${
                        item.status === "active" ? "text-teal-700" : "text-gray-400"
                      }`}
                    >
                      {item.status === "active" ? "Active" : "Inactive"}
                    </span> */}
                  </div>
                </td>

                {/* Direct Edit & Delete Buttons */}
                <td className="px-4 py-3 text-right">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => setModal({ type: "edit", item })}
                      className="flex items-center px-2 py-1.5 text-sm text-teal-700   rounded-lg transition-colors"
                    >
                      <Edit2 size={14} />
                     
                    </button>
                    <button
                      onClick={() => setModal({ type: "delete", item })}
                      className="flex items-center px-2 py-1.5 text-sm text-red-600  rounded-lg transition-colors"
                    >
                      <Trash2 size={14} />
                  
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modals */}
      {modal.type === "add" && <ReportAddModal onClose={closeModal} onAdd={(data) => { addReportType(data); closeModal(); }} />}
      {modal.type === "edit" && <ReportEditModal item={modal.item} onClose={closeModal} onSave={updateReportType} />}
      {modal.type === "delete" && <ReportDeleteModal item={modal.item} onClose={closeModal} onDelete={deleteReportType} />}
    </div>
  );
}

// ➤ Add Modal
function ReportAddModal({ onClose, onAdd }) {
  const [form, setForm] = useState({ title: "", description: "", status: "active" });

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-gradient-to-br from-orange-50 to-orange-200 shadow-2xl z-50 p-8">
        <button onClick={onClose} className="absolute top-4 left-2 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center">
          <X size={18} />
        </button>
        <h2 className="text-3xl font-bold text-teal-900 mb-8">Add Report Type</h2>
        <div className="bg-[#ebddcd] rounded-2xl p-6 space-y-6 border border-orange-200 shadow-sm">
          <div>
            <label className="text-sm font-semibold text-gray-700">Title *</label>
            <input className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Description *</label>
            <textarea className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Status</label>
            <select className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-4 pt-4">
            <button className="flex-1 px-4 py-3 border rounded-xl bg-white" onClick={onClose}>Cancel</button>
            <button className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-xl" onClick={() => { if (!form.title) return; onAdd(form); }}>Create</button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// ➤ Edit Modal
function ReportEditModal({ item, onClose, onSave }) {
  const [form, setForm] = useState({ title: item.title, description: item.description, status: item.status });

  return createPortal(
    <>
      <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-40" onClick={onClose} />
      <div className="fixed top-0 right-0 h-full w-full max-w-2xl bg-gradient-to-br from-orange-50 to-orange-200 shadow-2xl z-50 p-8">
        <button onClick={onClose} className="absolute top-2 left-4 w-6 h-6 rounded-full bg-black text-white flex items-center justify-center">
          <X size={18} />
        </button>
        <h2 className="text-3xl font-bold text-teal-900 mb-8">Edit Report Type</h2>
        <div className="bg-[#ebddcd] rounded-2xl p-6 space-y-6 border border-orange-200 shadow-sm">
          <div>
            <label className="text-sm font-semibold text-gray-700">Title *</label>
            <input className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Description *</label>
            <textarea className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-700">Status</label>
            <select className="w-full mt-2 border px-4 py-3 rounded-xl bg-white" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
          <div className="flex gap-4 pt-4">
            <button className="flex-1 px-4 py-3 border rounded-xl bg-white" onClick={onClose}>Cancel</button>
            <button className="flex-1 px-4 py-3 bg-teal-600 text-white rounded-xl" onClick={() => { onSave({ ...item, ...form }); onClose(); }}>Update</button>
          </div>
        </div>
      </div>
    </>,
    document.body
  );
}

// ➤ Delete Modal
function ReportDeleteModal({ item, onClose, onDelete }) {
  return createPortal(
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className="bg-white w-full max-w-md rounded-xl p-6 space-y-4">
        <div className="flex justify-between items-center">
          <h2 className="text-xl font-semibold text-red-600">Delete Report Type</h2>
          <button onClick={onClose}><X /></button>
        </div>
        <p>Are you sure you want to delete <strong>{item.title}</strong>?</p>
        <div className="flex justify-end gap-3">
          <button className="px-4 py-2" onClick={onClose}>Cancel</button>
          <button className="px-4 py-2 bg-red-600 text-white rounded-xl" onClick={() => { onDelete(item.id); onClose(); }}>Delete</button>
        </div>
      </div>
    </div>,
    document.body
  );
}